import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  BookOpen,
  Archive,
  Wrench,
  Monitor,
  Leaf,
  Recycle,
  RotateCcw,
  Sparkles,
  Weight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous faire gagner du temps, nous assurons la livraison de
            vos cartons de déménagement directement à {city.name}. Plus
            besoin de transporter des fournitures encombrantes ou de
            multiplier les déplacements&nbsp;: nous vous livrons tout le
            matériel nécessaire à l&apos;adresse de votre choix. Cartons,
            adhésifs, protections et accessoires d&apos;emballage sont
            expédiés rapidement afin que vous puissiez commencer vos
            préparatifs dans les meilleures conditions.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Livraison en circuit court",
              body: "Nous excluons les réseaux de messagerie généraux. C'est notre propre structure francilienne qui prend en charge, transporte et dépose vos équipements d'emballage, garantissant un soin optimal à chaque étape.",
              Icon: Truck,
            },
            {
              title: "Qualité géométrique préservée",
              body: "Préservés des intempéries et de l'humidité au sein de nos véhicules fermés, vos cartons arrivent dans un état de rigidité impeccable, immédiatement opérationnels pour l'assemblage et le scellage.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Charges lourdes (split editorial + 4 use cases) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Editorial left */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Weight className="h-3.5 w-3.5" strokeWidth={2.6} />
              Charges lourdes et denses
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Nos solutions pour vos contraintes extrêmes
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Certains équipements imposent des contraintes physiques
              extrêmes lors des manipulations. Pour y faire face, nos
              structures renforcées intègrent une double cannelure à haute
              densité.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Grâce à leur composition rigide, ces modules encaissent la
              pression sans jamais fléchir, maintenant la géométrie parfaite
              du colis. Résultat&nbsp;: aucun risque d&apos;affaissement
              du fond ni de déformation lors du portage ou de
              l&apos;empilement dans le camion.
            </p>

            {/* Spec stat strip */}
            <div className="mt-7 inline-flex items-center gap-4 bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] text-white rounded-2xl px-5 py-4">
              <span className="font-display font-extrabold text-3xl text-white leading-none">
                DC
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                  Double cannelure
                </div>
                <div className="text-[13px] text-white/75">
                  Standard industriel renforcé
                </div>
              </div>
            </div>
          </div>

          {/* Right: 4 use cases */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                title: "Livres et romans",
                body: "Format compact qui plafonne la charge sous les 20 kg pour préserver le dos.",
                Icon: BookOpen,
                to: "/produit/carton-a-livres",
                cta: "Carton à livres",
              },
              {
                title: "Archives administratives",
                body: "Volume calibré pour empiler dossiers suspendus et classeurs sans tassement.",
                Icon: Archive,
                to: "/produit/conteneur-boites-archives",
                cta: "Conteneur archives",
              },
              {
                title: "Outillage et bricolage",
                body: "Structure renforcée capable d'encaisser les charges denses sans déformation latérale.",
                Icon: Wrench,
                to: "/produit/carton-standard-renforce",
                cta: "Carton standard renforcé",
              },
              {
                title: "Matériel informatique",
                body: "Combinez nos cartons standards avec papier bulle pour neutraliser les vibrations.",
                Icon: Monitor,
                to: "/produit/papier-bulle-10m",
                cta: "Papier bulle 10 m",
              },
            ].map(({ title, body, Icon, to, cta }) => (
              <Link
                key={title}
                to={to}
                className="group block bg-[#EEF3F7] hover:bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] rounded-2xl p-5 transition-all"
                data-testid={`city-marnes-cta-${to.split("/").pop()}`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] group-hover:gap-2.5 transition-all">
                  {cta}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Performance circulaire (éco-conçue, bleu charte) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Performance circulaire
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre gamme éco-conçue et réutilisable
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Sécuriser vos biens tout en limitant votre empreinte écologique
            est au cœur de notre démarche. Nos contenants proviennent
            entièrement de la revalorisation de fibres végétales et sont
            intégralement biodégradables. Conçus pour être hautement
            réutilisables d&apos;un déménagement à un autre, ils
            s&apos;intègrent ensuite parfaitement dans les filières de
            recyclage standard.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            En adoptant notre matériel, vous profitez d&apos;une barrière
            de protection industrielle ultra-fiable tout en participant
            activement à la réduction des déchets jetables.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              stat: "100%",
              kicker: "Biodégradable",
              title: "Fibres végétales revalorisées",
              body: "Issues de filières responsables et de forêts gérées durablement, nos fibres garantissent une circularité complète.",
              Icon: Leaf,
            },
            {
              stat: "×N",
              kicker: "Réutilisable",
              title: "Hautement réutilisable",
              body: "Pensés pour servir d'un déménagement à un autre, vous pouvez les plier à plat et les retransmettre à un proche.",
              Icon: RotateCcw,
            },
            {
              stat: "Tri",
              kicker: "Filière standard",
              title: "Recyclage 100% intégré",
              body: "Vos cartons se déposent dans les bacs de tri sélectif et redeviennent de la matière première sans étape spécifique.",
              Icon: Recycle,
            },
          ].map(({ stat, kicker, title, body, Icon }) => (
            <article
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
            >
              <div className="bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] text-white p-5 md:p-6 relative overflow-hidden">
                <div
                  aria-hidden="true"
                  className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#237cb5]/30 blur-2xl"
                />
                <div className="relative flex items-end justify-between">
                  <div>
                    <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-white leading-none">
                      {stat}
                    </div>
                    <div className="mt-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                      {kicker}
                    </div>
                  </div>
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-white/10 border border-white/15 text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
              </div>
              <div className="p-5 md:p-6 flex-1">
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-reconditionnes"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-marnes-cta-reco"
          >
            <Recycle className="h-4 w-4" strokeWidth={2.6} />
            Voir nos cartons reconditionnés
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-marnes-cta-shop"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-marnes-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
